import React, {useEffect, useState} from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import DynamicAlert from "../helpers/dynamicAlert";
import {userProfileService} from "../service/userProfile/userProfile.service";

const SUBSCRIPTION_TYPES = [
    {
        key: "emailMarketing",
        statusField: "emailMarketingStatus",
        type: "EMAIL_MARKETING",
        label: "Email Marketing"
    },
    {
        key: "smsMarketing",
        statusField: "smsMarketingStatus",
        type: "SMS_MARKETING",
        label: "Text Message Marketing"
    },
    {
        key: "transactional",
        statusField: "smsTransactionalStatus",
        type: "TRANSACTIONAL",
        label: "Transactional Text Messaging"
    }
];

const IMPACT_MESSAGES = {
    emailMarketing: {
        subscribe: "You'll start receiving promotional emails about sales, new products, and offers. You can unsubscribe at any time.",
        unsubscribe: "You'll stop receiving promotional emails about sales, new products, and offers. You may miss out on future deals."
    },
    smsMarketing: {
        subscribe: "You'll start receiving promotional text messages about sales, new products, and offers. Message and data rates may apply. You can unsubscribe at any time.",
        unsubscribe: "You'll stop receiving promotional text messages about sales, new products, and offers. You may miss out on future deals."
    },
    transactional: {
        subscribe: "You'll start receiving essential account and order text messages, such as verification codes, password resets, and order updates.",
        unsubscribe: "You'll stop receiving essential account and order text messages, including verification codes, password resets, and order updates. This may affect your ability to complete actions like signing in or checking out."
    }
};

export const SubscriptionsPage = () => {
    const [status, setStatus] = useState({});
    const [loading, setLoading] = useState(true);
    const [togglingKey, setTogglingKey] = useState(null);
    const [alert, setAlert] = useState(null);
    const [pendingSubscription, setPendingSubscription] = useState(null);

    const setTempAlert = (msg, type = "info") => {
        setAlert({
            id: Date.now(),
            msg,
            type
        });
    };

    useEffect(() => {
        userProfileService.getSubscriptions()
            .then((data) => {
                if (!data || data?.error) {
                    setTempAlert(
                        data?.error || "Failed to load subscription status",
                        "danger"
                    );
                    setStatus({});
                    return;
                }

                const mappedStatus = SUBSCRIPTION_TYPES.reduce(
                    (acc, subscription) => {
                        acc[subscription.key] =
                            data[subscription.statusField] === "SUBSCRIBED";

                        return acc;
                    },
                    {}
                );

                setStatus(mappedStatus);
            })
            .catch(() => {
                setTempAlert("Failed to load subscription status", "danger");
                setStatus({});
            })
            .finally(() => {
                setLoading(false);
            });
    }, []);

    const handleToggleRequest = (subscription) => {
        setPendingSubscription(subscription);
    };

    const closeConfirmModal = () => {
        setPendingSubscription(null);
    };

    const handleConfirmToggle = () => {
        if (pendingSubscription) {
            handleToggle(pendingSubscription);
        }

        setPendingSubscription(null);
    };

    const handleToggle = (subscription) => {
        const currentStatus = !!status?.[subscription.key];
        const subscribe = !currentStatus;

        setTogglingKey(subscription.key);

        userProfileService.updateSubscription({
            channel: subscription.type,
            action: subscribe ? "SUBSCRIBE" : "UNSUBSCRIBE"
        })
            .then((data) => {
                if (data && !data?.error) {
                    setStatus((prev) => ({
                        ...prev,
                        [subscription.key]: subscribe
                    }));

                    setTempAlert(
                        `${subscription.label} ${
                            subscribe ? "subscribed" : "unsubscribed"
                        } successfully!`,
                        "success"
                    );
                } else {
                    setTempAlert(
                        data?.error ||
                        `Failed to update ${subscription.label}`,
                        "danger"
                    );
                }
            })
            .catch(() => {
                setTempAlert(`Failed to update ${subscription.label}`, "danger");
            })
            .finally(() => {
                setTogglingKey(null);
            });
    };

    return (
        <div className="mfa-body">
            <div className="mfa-container">
                <div className="text-center m-6-auto">
                    <DynamicAlert alert={alert}/>

                    <div className="p-1 py-2">
                        <div className="text-center m-6-auto section-title">
                            <h4>Communication Preferences</h4>
                        </div>

                        {loading && <p>Loading subscription status...</p>}

                        {!loading && (
                            <div
                                style={{
                                    marginBottom: "24px",
                                    padding: "16px 18px",
                                    border: "1px solid #e8e8e3",
                                    borderRadius: "12px",
                                    background: "#fafaf7",
                                    fontFamily: "Arial, sans-serif",
                                    textAlign: "left"
                                }}
                            >

                                <div
                                    style={{
                                        fontSize: "13px",
                                        color: "#6b6f68",
                                        lineHeight: 1.5,
                                        marginBottom: "12px"
                                    }}
                                >
                                    Manage how you receive emails and text messages.
                                </div>

                                <div
                                    style={{
                                        fontSize: "13px",
                                        color: "#444842",
                                        lineHeight: 1.6
                                    }}
                                >
                                    <span style={{fontWeight: 600, color: "#23261f"}}>Marketing:</span>{" "}
                                    Promotional emails and texts. Turn on or off anytime.
                                    <br/>
                                    <span style={{fontWeight: 600, color: "#23261f"}}>Transactional:</span>{" "}
                                    Essential account and service messages, such as verification codes and password resets.
                                </div>
                            </div>
                        )}

                        {!loading && (
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "20px"
                                }}
                            >
                                {SUBSCRIPTION_TYPES.map((subscription) => {
                                    const isSubscribed = !!status?.[subscription.key];
                                    const isToggling = togglingKey === subscription.key;

                                    return (
                                        <div
                                            key={subscription.key}
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between"
                                            }}
                                        >
                                            <span>{subscription.label}</span>

                                            <label className="switch" aria-label={`Toggle ${subscription.label}`}>
                                                <input
                                                    type="checkbox"
                                                    checked={isSubscribed}
                                                    disabled={isToggling}
                                                    onChange={() => handleToggleRequest(subscription)}
                                                />

                                                <span className="slider"/>
                                            </label>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <Modal show={!!pendingSubscription} onHide={closeConfirmModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title style={{width: "100%", textAlign: "center"}}>Confirm Preference Change</Modal.Title>
                </Modal.Header>

                {pendingSubscription && (
                    <Modal.Body>
                        <p>
                            You are about to{" "}
                            <strong>
                                {status?.[pendingSubscription.key] ? "unsubscribe from" : "subscribe to"}
                            </strong>{" "}
                            {pendingSubscription.label}.
                        </p>

                        <p>
                            {
                                IMPACT_MESSAGES[pendingSubscription.key][
                                    status?.[pendingSubscription.key] ? "unsubscribe" : "subscribe"
                                ]
                            }
                        </p>

                        <p className="text-muted" style={{fontSize: "13px"}}>
                            By clicking Confirm, you consent to this change to your communication preferences.
                        </p>
                    </Modal.Body>
                )}

                <Modal.Footer style={{width: "100%", textAlign: "center"}}>
                    <Button variant="secondary" onClick={closeConfirmModal}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleConfirmToggle}>
                        Confirm
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default SubscriptionsPage;
